import { graphql } from '~/graphql/generated/gql';

export const fetchArticleAssets = graphql(`
  query fetchArticleAssets($first: Int, $page: Int, $filters: AdminLearningAssetFilters, $orderBy: [AdminLearningAssetOrder!]) {
    learningAssetsList(first: $first, page: $page, filters: $filters, orderBy: $orderBy) {
      data {
        id
        name
        slug
        publishedAt
        publishedContentId
        draftContentId
        considerationAreas {
          slug
        }
        draftContent {
          id
          articleData {
            template
          }
        }
        curriculumTags {
          ...AdminCurriculumTagFields
        }
      }
      paginatorInfo {
        ...PaginatorInfoFields
      }
    }
  }
`);

export const fetchArticleQuizCollection = graphql(`
  query fetchArticleQuizCollection($id: ID!) {
    learningAssetCollections(first: 1, filters: { ids: [$id] }) {
      data {
        id
        name
        description
        slug
        totalCoins
        coverImage {
          id
          publicUrl
        }
        learningAssets {
          id
          name
          publishedAt
        }
      }
    }
  }
`);

export const fetchArticleKeepReading = graphql(`
  query fetchArticleKeepReading($first: Int, $filters: LearningAssetFilters) {
    learningAssets(first: $first, filters: $filters) {
      data {
        id
        name
        slug
        considerationAreas {
          slug
        }
      }
    }
  }
`);

export const articleDataFragment = graphql(`
  fragment ArticleDataFields on ArticleData {
    template
    standfirst
    body
    readingTimeMinutes
    authorName
    stepByStepGuide {
      steps {
        title
        body
      }
    }
    careerPathwayMap {
      tracksHeading
      tracksIntro
      tracks {
        title
        description
        mostCommon
      }
      pathwayStages {
        heading
        intro
        rows {
          role
          description
          timeToFirstRole
          salaryAu
          salaryUs
        }
      }
      studyRoutesHeading
      studyRoutesIntro
      studyRoutes {
        title
        subtitle
        duration
        cost
        pros
        cons
      }
      decisionGuideHeading
      decisionGuideBody
      faqs {
        question
        answer
      }
    }
    rankedCards {
      variant
      cards {
        title
        badge
        fields {
          label
          value
        }
        salaryAu
        salaryUs
        demandLevel
        demandLabel
        demandNote
      }
      synthesisHeading
      synthesisBody
      faqs {
        question
        answer
      }
    }
    salaryGuide {
      methodologyNote
      entryRoles {
        role
        description
        timeToFirstRole
        salaryAu
        salaryUs
      }
      roles {
        title
        badge
        entryAu
        entryUs
        midAu
        midUs
        seniorAu
        seniorUs
        note
      }
      factorsHeading
      factorsIntro
      factors {
        title
        badge
        body
      }
      bridgeHeading
      bridgeBody
      faqs {
        question
        answer
      }
    }
    explainer {
      rolesHeading
      rolesIntro
      roles {
        title
        outlook
        description
      }
      checklistHeading
      checklistIntro
      checklist {
        title
        body
      }
      resourcesHeading
      resources {
        title
        url
      }
      usHeading
      usBody
      closingHeading
      closingBody
      faqs {
        question
        answer
      }
    }
    quizLanding {
      quizCollectionId
      resultsHeading
      resultsIntro
      resultTypes {
        title
        description
        mapsTo
        illustration
        accentColor
      }
      stepsHeading
      nextSteps {
        title
        body
        links {
          label
          url
        }
      }
      closingHeading
      closingBody
    }
    dayInTheLife {
      disclaimer
      entries {
        time
        title
        body
      }
      honestyHeading
      honestyBody
      closingHeading
      closingBody
      faqs {
        question
        answer
      }
    }
    practitionerStories {
      profiles {
        name
        age
        role
        city
        pullQuote
        narrative
        youngerSelf
      }
      closingHeading
      closingBody
    }
    comparisonGuide {
      density
      options {
        name
        duration
        whatItIs
        whoSuitsIt
        costAu
        costUs
        realTalk
        bestFor
      }
      decisionHeading
      decisionIntro
      decisionMap {
        who
        pick
      }
      closingHeading
      closingBody
      faqs {
        question
        answer
      }
    }
  }
`);

export const articleLearningAssetFragment = graphql(`
  fragment ArticleLearningAssetFields on LearningAsset {
    id
    name
    slug
    publishedAt
    lastPublishedAt
    firstPublishedAt
    publishedContentId
    draftContentId
    coverImage {
      ...PublicFileFields
    }
    meta {
      title
      description
    }
    considerationAreas {
      id
      label
      slug
    }
    publishedContent {
      id
      articleData {
        ...ArticleDataFields
      }
    }
    draftContent {
      id
      articleData {
        ...ArticleDataFields
      }
    }
  }
`);

export const adminArticleLearningAssetFragment = graphql(`
  fragment AdminArticleLearningAssetFields on AdminLearningAsset {
    id
    name
    slug
    publishedAt
    displayPublishedAt
    firstPublishedAt
    publishedContentId
    draftContentId
    coverImage {
      ...AdminContentFileFields
    }
    partnerId
    partner {
      id
      name
    }
    meta {
      title
      description
    }
    considerationAreas {
      id
      label
      slug
    }
    curriculumTags {
      ...AdminCurriculumTagFields
    }
    publishedContent {
      id
      articleData {
        ...ArticleDataFields
      }
    }
    draftContent {
      id
      articleData {
        ...ArticleDataFields
      }
    }
  }
`);

export const fetchAdminArticleLearningAsset = graphql(`
  query fetchAdminArticleLearningAsset($filters: AdminLearningAssetFilters) {
    learningAssetsList(filters: $filters) {
      data {
        ...AdminArticleLearningAssetFields
      }
    }
  }
`);

export const fetchArticleLearningAsset = graphql(`
  query fetchArticleLearningAsset($filters: LearningAssetFilters) {
    learningAssets(filters: $filters) {
      data {
        ...ArticleLearningAssetFields
      }
    }
  }
`);

export const upsertArticleMutation = graphql(`
  mutation upsertArticle($input: UpsertLearningAssetInput!) {
    upsertLearningAsset(input: $input) {
      learningAsset {
        ...AdminArticleLearningAssetFields
      }
    }
  }
`);

export const publishArticleMutation = graphql(`
  mutation publishArticle($input: PublishLearningAssetInput!) {
    publishLearningAsset(input: $input) {
      success
      learningAsset {
        id
        publishedAt
        publishedContentId
        draftContentId
      }
    }
  }
`);

export const unpublishArticleMutation = graphql(`
  mutation unpublishArticle($input: UnpublishLearningAssetInput!) {
    unpublishLearningAsset(input: $input) {
      success
      learningAsset {
        id
        publishedAt
        publishedContentId
        draftContentId
      }
    }
  }
`);

// One published article as a card in a row (`toArticleCards`): a hub, the Latest row, the Advice view.
export const articleCardFieldsFragment = graphql(`
  fragment ArticleCardFields on LearningAsset {
    id
    name
    slug
    coverImage {
      publicUrl
    }
    considerationAreas {
      slug
    }
    partner {
      id
      name
      iconFile {
        publicUrl
      }
    }
    publishedContent {
      id
      articleData {
        template
        readingTimeMinutes
      }
    }
  }
`);

export const fetchHubArticles = graphql(`
  query fetchHubArticles($considerationAreaSlug: String!, $first: Int!) {
    learningAssets(
      filters: { considerationAreaSlug: $considerationAreaSlug, types: [ARTICLE], publishedOnly: true }
      orderBy: [{ column: PUBLISHED_AT, order: DESC }]
      first: $first
    ) {
      data {
        ...ArticleCardFields
      }
    }
  }
`);

export const fetchLatestArticles = graphql(`
  query fetchLatestArticles($first: Int!) {
    learningAssets(
      filters: { types: [ARTICLE], publishedOnly: true }
      orderBy: [{ column: PUBLISHED_AT, order: DESC }]
      first: $first
    ) {
      data {
        ...ArticleCardFields
      }
    }
  }
`);

export const fetchArticlesCatalogue = graphql(`
  query fetchArticlesCatalogue($page: Int!, $first: Int!, $keyword: String, $hasPartner: Boolean) {
    learningAssets(
      filters: { types: [ARTICLE], publishedOnly: true, keyword: $keyword, hasPartner: $hasPartner }
      orderBy: [{ column: PUBLISHED_AT, order: DESC }]
      page: $page
      first: $first
    ) {
      data {
        ...ArticleCardFields
      }
      paginatorInfo {
        currentPage
        hasMorePages
      }
    }
  }
`);

// The articles admins have placed in the Featured category (/content/categories), in that order.
export const fetchFeaturedArticles = graphql(`
  query fetchFeaturedArticles {
    learningAssetsInCategory(category: FEATURED, types: [ARTICLE]) {
      ...ArticleCardFields
    }
  }
`);

// The articles admins have placed in Featured media (/content/categories), in that order, for
// the Media centre. The first leads the page, so each carries its standfirst and byline.
export const fetchFeaturedMedia = graphql(`
  query fetchFeaturedMedia {
    learningAssetsInCategory(category: MEDIA, types: [ARTICLE]) {
      id
      publishedAt
      ...ArticleCardFields
      publishedContent {
        id
        articleData {
          standfirst
          authorName
        }
      }
    }
  }
`);

// The newest articles a partner other than Anyway wrote, for the Advice partner rail.
export const fetchPartnerArticles = graphql(`
  query fetchPartnerArticles($first: Int!) {
    learningAssets(
      filters: { types: [ARTICLE], publishedOnly: true, hasPartner: true }
      orderBy: [{ column: PUBLISHED_AT, order: DESC }]
      first: $first
    ) {
      data {
        ...ArticleCardFields
      }
    }
  }
`);
